import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Star } from "lucide-react";
import { Logo } from "./Logo";

const EASE = [0.22, 1, 0.36, 1];
const BACKDROP_IMG =
  "https://images.unsplash.com/photo-1505843513577-22bb7d21e455?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3VzZSUyMGR1c2slMjB0d2lsaWdodCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTA4NzUwNTB8MA&ixlib=rb-4.1.0&q=85";
const CARD_IMG =
  "https://images.unsplash.com/photo-1760633549227-901e0c3cf9d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxibGFjayUyMGZhbWlseSUyMHBvcnRyYWl0JTIwZ29sZGVuJTIwaG91ciUyMGhhcHB5fGVufDB8fHx8MTc5MDg3NTA1MHww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 7, ry: px * 9 });
  };

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pb-24 pt-36"
    >
      <div className="absolute inset-0 -z-10">
        <motion.img
          style={{ y: imgY }}
          src={BACKDROP_IMG}
          alt=""
          className="h-[115%] w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--ns-bg)]/60 via-[color:var(--ns-bg)]/85 to-[color:var(--ns-bg)]" />
        <div className="absolute -top-44 right-[-8%] h-[34rem] w-[34rem] rounded-full bg-amber-500/10 blur-[130px]" />
      </div>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-8 lg:grid-cols-12 lg:px-12">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7, ease: EASE }}
            data-testid="hero-eyebrow"
            className="mb-7 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[color:var(--ns-gold)]"
          >
            <span className="h-px w-10 bg-[color:var(--ns-gold)]/70" />
            Hunt Shield Insurance Group · Atlanta, GA
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-[color:var(--ns-ink)] sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.25}>Coverage that</MaskedLine>
            <MaskedLine delay={0.37}>stands as</MaskedLine>
            <MaskedLine delay={0.49}>
              <em className="text-[color:var(--ns-gold)]">your Shield.</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8, ease: EASE }}
            data-testid="hero-subcopy"
            className="mt-7 max-w-xl text-base leading-relaxed text-[color:var(--ns-muted)] sm:text-lg"
          >
            Home &amp; Auto, Life &amp; Health, and Medicaid guidance — from an independent
            Atlanta agent who answers her own phone.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#quote"
              data-testid="hero-quote-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[color:var(--ns-gold)] px-7 py-3.5 font-semibold text-[#0B0F17] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-500/25"
            >
              Request a Quote
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#coverage"
              data-testid="hero-coverage-btn"
              className="inline-flex items-center rounded-full border border-[color:var(--ns-line)] px-7 py-3.5 font-medium text-[color:var(--ns-ink)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[color:var(--ns-gold)]/50 hover:text-[color:var(--ns-gold)]"
            >
              Explore Coverage
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.9 }}
            className="mt-12 flex items-center gap-3 text-sm text-[color:var(--ns-muted)]"
          >
            <ShieldCheck size={18} className="text-[color:var(--ns-gold)]" />
            <span data-testid="hero-trust-line">
              Independent guidance across three lines — one conversation, zero pressure.
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          className="hidden lg:col-span-5 lg:block"
          initial={{ opacity: 0, scale: 0.94, y: 28 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 1.1, ease: EASE }}
        >
          <div
            data-testid="hero-tilt-card"
            onMouseMove={onMove}
            onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
            style={{ transform: `perspective(1100px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)` }}
            className="relative transition-transform duration-200 ease-out"
          >
            <div className="absolute -inset-10 -z-10 flex items-center justify-center opacity-25">
              <div className="animate-[spin_46s_linear_infinite]">
                <Logo size={380} testId="hero-star-ring" />
              </div>
            </div>
            <div className="overflow-hidden rounded-[2rem] border border-[color:var(--ns-line)] shadow-2xl shadow-black/50">
              <img
                src={CARD_IMG}
                alt="A Black family together at golden hour, protected under Hunt Shield coverage"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-t from-[#0B0F17]/70 via-transparent to-transparent" />
            </div>
            <div
              data-testid="hero-agent-badge"
              className="absolute -bottom-6 -left-6 flex items-center gap-4 rounded-2xl border border-[color:var(--ns-line)] bg-[color:var(--ns-bg-2)]/90 px-5 py-4 shadow-2xl backdrop-blur-xl"
            >
              <Logo size={40} testId="hero-badge-star" />
              <div>
                <p className="font-serif text-base text-[color:var(--ns-ink)]">Renee Black</p>
                <p className="flex items-center gap-1 text-xs text-[color:var(--ns-muted)]">
                  Independent Agent
                  <span className="mx-1 inline-flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={10} className="fill-[color:var(--ns-gold)] text-[color:var(--ns-gold)]" />
                    ))}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
